/**
 * Step 1's post-import review. Once the schedule is imported and the draft
 * tournament is built, this pauses to show what we got — event name, dates, the
 * one tournament location, its fields, and game count — so the coach can
 * sanity-check before matching teams.
 *
 * The name, dates, and location are editable here and save straight to the
 * (still-hidden draft) event. The tournament carries a single geocoded location
 * (the complex/park); each game is just a field-name label under it, shown
 * read-only. Continuing advances to the Teams step.
 */

import { useMemo, useState, useEffect } from 'react';
import { getEvent, updateEvent } from '../../../../../services/calendar';
import type { CalendarEventResponse } from '../../../../../types/api';
import { DateTimeEditor, LocationField, type PickedLocation } from '../ScheduleFields';
import {
  utcDateInputValue,
  exclusiveEndToInclusiveDate,
} from '../../../../../utils/calendarTime';
import { useTournamentGames } from './useTournamentGames';
import { summarizeImport } from './importSummary';
import { WhatHappensNextRail } from './WhatHappensNextRail';
import { WizardFooter } from './WizardFooter';

/** Human date label for the loaded event: "Jul 15 – 18, 2026" (all-day) or with times. */
function formatEventDates(event: CalendarEventResponse): string {
  const day = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  if (event.isAllDay) {
    const start = utcDateInputValue(event.startsAtUtc);
    const end = exclusiveEndToInclusiveDate(event.endsAtUtc);
    return start === end
      ? day(`${start}T00:00:00Z`)
      : `${day(`${start}T00:00:00Z`)} – ${day(`${end}T00:00:00Z`)}`;
  }
  const tz = event.timeZone || 'America/New_York';
  const fmt = (iso: string) =>
    new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: tz,
    });
  return `${fmt(event.startsAtUtc)} – ${fmt(event.endsAtUtc)}`;
}

interface ImportOverviewProps {
  calendarEventId: string;
  eventName: string;
  onNext: () => void;
  onBack?: () => void;
  canGoBack?: boolean;
  /** Bubble a saved rename up so the rest of the wizard reflects it. */
  onRenamed?: (name: string) => void;
}

export function ImportOverview({
  calendarEventId,
  eventName,
  onNext,
  onBack,
  canGoBack,
  onRenamed,
}: ImportOverviewProps) {
  const gamesState = useTournamentGames(calendarEventId);
  const summary = useMemo(
    () => (gamesState.status === 'ready' ? summarizeImport(gamesState.games) : null),
    [gamesState],
  );

  // The full event, loaded once so a title save can PUT the whole object (the update is a full
  // replace) without disturbing the draft flag, dates, or location.
  const [event, setEvent] = useState<CalendarEventResponse | null>(null);
  const [title, setTitle] = useState(eventName);
  const [savingTitle, setSavingTitle] = useState(false);
  const [titleError, setTitleError] = useState('');

  useEffect(() => {
    let active = true;
    getEvent(calendarEventId)
      .then((e) => {
        if (!active) return;
        setEvent(e);
        setTitle(e.title);
      })
      .catch(() => {
        /* non-fatal: the title stays read-from-props until this resolves */
      });
    return () => {
      active = false;
    };
  }, [calendarEventId]);

  const titleDirty = event !== null && title.trim().length > 0 && title.trim() !== event.title;

  const saveTitle = async () => {
    if (!event || !titleDirty) return;
    const next = title.trim();
    setSavingTitle(true);
    setTitleError('');
    try {
      // Full-object update (the API replaces the event); omit assigneeUserIds to leave assignees
      // untouched. IsDraft is server-preserved, so this does not publish the hidden draft.
      const updated = await updateEvent(calendarEventId, {
        title: next,
        eventType: event.eventType,
        startsAtUtc: event.startsAtUtc,
        endsAtUtc: event.endsAtUtc,
        isAllDay: event.isAllDay,
        timeZone: event.timeZone,
        locationName: event.locationName,
        locationAddress: event.locationAddress,
        locationLat: event.locationLat,
        locationLng: event.locationLng,
        locationPlaceId: event.locationPlaceId,
        itineraryUrl: event.itineraryUrl,
        notes: event.notes,
      });
      setEvent(updated);
      setTitle(updated.title);
      onRenamed?.(updated.title);
    } catch {
      setTitleError("Couldn't save the name. Please try again.");
    } finally {
      setSavingTitle(false);
    }
  };

  // Whether the date/time editor is open, plus save/error state.
  const [editingDates, setEditingDates] = useState(false);
  const [savingDates, setSavingDates] = useState(false);
  const [datesError, setDatesError] = useState('');

  const saveDates = async (patch: Partial<CalendarEventResponse>) => {
    if (!event) return;
    setSavingDates(true);
    setDatesError('');
    try {
      // Full-object update (the API replaces the event); merge the date patch over the
      // loaded event so nothing else changes. IsDraft is server-preserved.
      const updated = await updateEvent(calendarEventId, {
        title: event.title,
        eventType: event.eventType,
        startsAtUtc: patch.startsAtUtc ?? event.startsAtUtc,
        endsAtUtc: patch.endsAtUtc ?? event.endsAtUtc,
        isAllDay: patch.isAllDay ?? event.isAllDay,
        timeZone: patch.timeZone ?? event.timeZone,
        locationName: event.locationName,
        locationAddress: event.locationAddress,
        locationLat: event.locationLat,
        locationLng: event.locationLng,
        locationPlaceId: event.locationPlaceId,
        itineraryUrl: event.itineraryUrl,
        notes: event.notes,
      });
      setEvent(updated);
      setEditingDates(false);
    } catch {
      setDatesError("Couldn't save the dates. Please try again.");
    } finally {
      setSavingDates(false);
    }
  };

  // Event-location save state. The tournament carries one geocoded location (the
  // complex/park); each game is just a field-name label under it.
  const [savingLocation, setSavingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');

  const saveLocation = async (picked: PickedLocation) => {
    if (!event) return;
    setSavingLocation(true);
    setLocationError('');
    try {
      // Full-object update (the API replaces the event); merge the location over the
      // loaded event so nothing else changes. IsDraft is server-preserved.
      const updated = await updateEvent(calendarEventId, {
        title: event.title,
        eventType: event.eventType,
        startsAtUtc: event.startsAtUtc,
        endsAtUtc: event.endsAtUtc,
        isAllDay: event.isAllDay,
        timeZone: event.timeZone,
        locationName: picked.name,
        locationAddress: picked.address,
        locationLat: picked.lat,
        locationLng: picked.lng,
        locationPlaceId: picked.placeId,
        itineraryUrl: event.itineraryUrl,
        notes: event.notes,
      });
      setEvent(updated);
    } catch {
      setLocationError("Couldn't save the location. Please try again.");
    } finally {
      setSavingLocation(false);
    }
  };

  return (
    <div className="wiz-cols">
      <div className="wiz-card wiz-card--pad">
        <h2 className="wiz-step-title">Schedule imported</h2>
        <p className="wiz-sub">
          Here's what we pulled in. Review it (fix the name or a venue if needed), then continue to
          match teams and follow players.
        </p>

        <WizardFooter
          onBack={onBack}
          canGoBack={canGoBack}
          legend="You can fix any mis-parsed teams or games in the next steps."
        >
          <button
            type="button"
            className="wiz-btn wiz-btn--primary"
            onClick={onNext}
            disabled={gamesState.status === 'loading'}
            data-testid="overview-next"
          >
            Next: Match Teams →
          </button>
        </WizardFooter>

        <div className="wiz-field">
          <label htmlFor="overview-title">Event name</label>
          <div className="wiz-inline-edit">
            <input
              id="overview-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              data-testid="overview-title"
            />
            {titleDirty && (
              <button
                type="button"
                className="wiz-btn wiz-btn--primary"
                onClick={saveTitle}
                disabled={savingTitle}
                data-testid="overview-title-save"
              >
                {savingTitle ? 'Saving...' : 'Save'}
              </button>
            )}
          </div>
          {titleError && (
            <p className="wiz-error" role="alert">
              {titleError}
            </p>
          )}
        </div>

        {event && (
          <div className="wiz-field">
            <label>Dates & time</label>
            {editingDates ? (
              <DateTimeEditor
                event={event}
                onSave={saveDates}
                onCancel={() => setEditingDates(false)}
              />
            ) : (
              <div className="wiz-inline-edit">
                <span className="wiz-dates-label" data-testid="overview-dates">
                  {formatEventDates(event)}
                  {event.isAllDay ? ' · All-day' : ''}
                </span>
                <button
                  type="button"
                  className="wiz-link-btn"
                  onClick={() => setEditingDates(true)}
                  disabled={savingDates}
                  data-testid="overview-dates-edit"
                >
                  Edit
                </button>
              </div>
            )}
            {datesError && (
              <p className="wiz-error" role="alert">
                {datesError}
              </p>
            )}
          </div>
        )}

        {gamesState.status === 'loading' && (
          <div className="wiz-progress" data-testid="overview-loading">
            <span className="wiz-spinner" aria-hidden="true" />
            <span>Loading your event...</span>
          </div>
        )}
        {gamesState.status === 'error' && (
          <p className="wiz-error" role="alert">
            {gamesState.message}
          </p>
        )}

        {summary && (
          <div className="wiz-overview" data-testid="import-overview">
            <div className="wiz-overview__meta">
              {summary.dateRangeLabel && <span>{summary.dateRangeLabel}</span>}
              {summary.dateRangeLabel && <span aria-hidden="true"> · </span>}
              <span>
                {summary.gamesCount} {summary.gamesCount === 1 ? 'game' : 'games'}
              </span>
            </div>

            {event && (
              <div className="wiz-field">
                <label>Location</label>
                <LocationField
                  name={event.locationName ?? ''}
                  placeholder="Set the tournament complex or park"
                  onPick={saveLocation}
                />
                {savingLocation && <p className="wiz-sub">Saving location...</p>}
                {!savingLocation && event.locationAddress && (
                  <p className="wiz-sub" data-testid="overview-location-addr">
                    📍 {event.locationAddress}
                  </p>
                )}
                {locationError && (
                  <p className="wiz-error" role="alert">
                    {locationError}
                  </p>
                )}
              </div>
            )}

            {summary.venues.length > 0 && (
              <div className="wiz-overview__venues">
                <div className="wiz-field__label">
                  {summary.venues.length === 1 ? 'Field' : 'Fields'}
                </div>
                <ul className="wiz-overview__field-list">
                  {summary.venues.map((v) => (
                    <li key={v.name} className="wiz-overview__field-chip">
                      {v.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <WhatHappensNextRail />
    </div>
  );
}
